import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import StatusPill from '../../components/ui/StatusPill';
import EmptyState from '../../components/ui/EmptyState';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import PageHeader from '../../components/ui/PageHeader';
import Modal from '../../components/ui/Modal';
import { toast } from '../../components/ui/Toast';
import {
  IconFlame,
  IconPlus,
  IconSearch,
  IconCheck,
  IconAlertTriangle,
} from '@tabler/icons-react';

const HAZARD_TYPES = ['Flammable', 'Toxic', 'Corrosive', 'Irritant', 'Oxidising', 'Harmful', 'Environmental', 'Explosive'];
const RISK_LEVELS = ['low', 'medium', 'high'];

export default function CoshhRegister() {
  const { orgId, userProfile } = useAuth();
  const navigate = useNavigate();
  const [substances, setSubstances] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const [productName, setProductName] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [selectedHazards, setSelectedHazards] = useState([]);
  const [riskLevel, setRiskLevel] = useState('medium');
  const [siteId, setSiteId] = useState('');
  const [storageRequirements, setStorageRequirements] = useState('');
  const [disposalMethod, setDisposalMethod] = useState('');
  const [ppe, setPpe] = useState('');

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const [substancesSnap, sitesSnap] = await Promise.all([
          getDocs(collection(db, 'organisations', orgId, 'coshh')),
          getDocs(collection(db, 'organisations', orgId, 'sites')),
        ]);
        setSubstances(substancesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setSites(sitesSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [orgId]);

  const toggleHazard = (h) => {
    setSelectedHazards(prev =>
      prev.includes(h) ? prev.filter(x => x !== h) : [...prev, h]
    );
  };

  const handleAdd = async () => {
    if (!productName.trim()) { toast.error('Product name is required'); return; }
    setSaving(true);
    try {
      const site = sites.find(s => s.id === siteId);
      const newDoc = {
        productName: productName.trim(),
        manufacturer: manufacturer.trim() || null,
        hazardType: selectedHazards,
        riskLevel,
        siteId: siteId || null,
        siteName: site?.name || null,
        storageRequirements: storageRequirements.trim() || null,
        disposalMethod: disposalMethod.trim() || null,
        ppe: ppe.trim() ? ppe.split(',').map(p => p.trim()) : [],
        datasheetURL: null,
        lastReviewDate: new Date().toISOString().split('T')[0],
        nextReviewDate: null,
        createdBy: userProfile?.id,
        createdAt: serverTimestamp(),
      };
      const ref = await addDoc(collection(db, 'organisations', orgId, 'coshh'), newDoc);
      setSubstances(prev => [...prev, { id: ref.id, ...newDoc }]);
      setShowAddModal(false);
      setProductName(''); setManufacturer(''); setSelectedHazards([]);
      setRiskLevel('medium'); setSiteId(''); setStorageRequirements('');
      setDisposalMethod(''); setPpe('');
      toast.success('Substance added to COSHH register');
    } catch (err) {
      console.error(err);
      toast.error('Failed to add substance');
    }
    setSaving(false);
  };

  const filtered = substances.filter(s => {
    const q = search.toLowerCase();
    return !q || s.productName?.toLowerCase().includes(q) || s.manufacturer?.toLowerCase().includes(q);
  });

  const highRisk = substances.filter(s => s.riskLevel === 'high').length;

  const riskColour = (r) => {
    if (r === 'high') return 'var(--color-danger)';
    if (r === 'medium') return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <PageHeader
          title="COSHH register"
          subtitle={`${substances.length} substance${substances.length !== 1 ? 's' : ''} · ${highRisk} high risk`}
          actions={
            <Button variant="primary" icon={<IconPlus size={14} stroke={1.5} />} onClick={() => setShowAddModal(true)}>
              Add substance
            </Button>
          }
        />

        {highRisk > 0 && (
          <div style={{ background: 'var(--color-danger-light)', border: '0.5px solid var(--color-danger)', borderRadius: 'var(--radius-lg)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <IconAlertTriangle size={16} color="var(--color-danger)" stroke={1.5} />
            <div style={{ fontSize: 13, color: 'var(--color-danger)' }}>
              {highRisk} high risk substance{highRisk !== 1 ? 's' : ''} in the register — ensure all operators are briefed
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 20 }}>
          {[
            { label: 'Total substances', value: substances.length, colour: 'var(--color-text-primary)' },
            { label: 'High risk', value: highRisk, colour: highRisk > 0 ? 'var(--color-danger)' : 'var(--color-text-tertiary)' },
            { label: 'Medium risk', value: substances.filter(s => s.riskLevel === 'medium').length, colour: 'var(--color-warning)' },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '14px 16px' }}>
              <div style={{ fontSize: 28, fontWeight: 400, color: s.colour, marginBottom: 4 }}>{s.value}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ position: 'relative', marginBottom: 16 }}>
          <IconSearch size={14} stroke={1.5} color="var(--color-text-tertiary)" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            placeholder="Search substances..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: 32, width: '100%', boxSizing: 'border-box' }}
          />
        </div>

        <Card padding="0">
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 0.8fr 60px', padding: '8px 16px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)' }}>
            {['Product', 'Manufacturer', 'Hazards', 'Risk', ''].map(h => (
              <div key={h} style={{ fontSize: 10, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{h}</div>
            ))}
          </div>
          {loading ? (
            <LoadingSkeleton rows={5} />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={<IconFlame size={32} stroke={1.5} />}
              title="No substances recorded"
              message={search ? 'Try adjusting your search' : 'Add your first substance to the COSHH register'}
              action={!search && <Button variant="primary" icon={<IconPlus size={14} stroke={1.5} />} onClick={() => setShowAddModal(true)}>Add substance</Button>}
            />
          ) : filtered.map((sub, i) => (
            <div
              key={sub.id}
              onClick={() => navigate(`/coshh/${sub.id}`)}
              style={{
                display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 0.8fr 60px',
                padding: '12px 16px',
                borderBottom: i < filtered.length - 1 ? '0.5px solid var(--color-border-light)' : 'none',
                cursor: 'pointer', alignItems: 'center',
                background: sub.riskLevel === 'high' ? 'var(--color-danger-light)' : 'transparent',
              }}
              onMouseEnter={e => sub.riskLevel !== 'high' && (e.currentTarget.style.background = 'var(--color-bg-secondary)')}
              onMouseLeave={e => sub.riskLevel !== 'high' && (e.currentTarget.style.background = 'transparent')}
            >
              <div>
                <div style={{ fontSize: 13, color: 'var(--color-text-primary)', marginBottom: 2 }}>{sub.productName}</div>
                {sub.siteName && <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{sub.siteName}</div>}
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{sub.manufacturer || '—'}</div>
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                {sub.hazardType?.slice(0, 2).join(', ') || '—'}
                {sub.hazardType?.length > 2 ? ` +${sub.hazardType.length - 2}` : ''}
              </div>
              <div style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: riskColour(sub.riskLevel) + '18', color: riskColour(sub.riskLevel), textTransform: 'capitalize', display: 'inline-block' }}>
                {sub.riskLevel || 'low'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-brand)', textAlign: 'right' }}>View</div>
            </div>
          ))}
        </Card>
      </div>

      <Modal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add substance"
        size="md"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleAdd} disabled={saving} loading={saving} icon={<IconCheck size={14} stroke={1.5} />}>Add to register</Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Product name <span style={{ color: 'var(--color-danger)' }}>*</span></label>
              <input type="text" placeholder="e.g. Round-Up Weedkiller" value={productName} onChange={e => setProductName(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Manufacturer</label>
              <input type="text" placeholder="e.g. Bayer" value={manufacturer} onChange={e => setManufacturer(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 8 }}>Hazard types</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {HAZARD_TYPES.map(h => (
                <div
                  key={h}
                  onClick={() => toggleHazard(h)}
                  style={{
                    padding: '4px 10px', borderRadius: 'var(--radius-md)', fontSize: 12, cursor: 'pointer',
                    border: '0.5px solid',
                    borderColor: selectedHazards.includes(h) ? 'var(--color-danger)' : 'var(--color-border-medium)',
                    background: selectedHazards.includes(h) ? 'var(--color-danger-light)' : 'transparent',
                    color: selectedHazards.includes(h) ? 'var(--color-danger)' : 'var(--color-text-secondary)',
                    transition: 'all 0.15s',
                  }}
                >
                  {h}
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Risk level</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {RISK_LEVELS.map(r => (
                  <div
                    key={r}
                    onClick={() => setRiskLevel(r)}
                    style={{
                      flex: 1, padding: '7px', borderRadius: 'var(--radius-md)', fontSize: 12, cursor: 'pointer', textAlign: 'center',
                      border: '0.5px solid',
                      borderColor: riskLevel === r ? riskColour(r) : 'var(--color-border-medium)',
                      background: riskLevel === r ? riskColour(r) + '18' : 'transparent',
                      color: riskLevel === r ? riskColour(r) : 'var(--color-text-secondary)',
                      textTransform: 'capitalize', transition: 'all 0.15s',
                    }}
                  >
                    {r}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Site</label>
              <select value={siteId} onChange={e => setSiteId(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }}>
                <option value="">All sites</option>
                {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Required PPE <span style={{ color: 'var(--color-text-tertiary)' }}>— comma separated</span></label>
            <input type="text" placeholder="e.g. Gloves, Eye protection, Respirator" value={ppe} onChange={e => setPpe(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Storage requirements</label>
            <textarea placeholder="e.g. Store in a cool, dry place away from heat sources" value={storageRequirements} onChange={e => setStorageRequirements(e.target.value)} rows={2} style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Disposal method</label>
            <textarea placeholder="e.g. Dispose of in accordance with local regulations" value={disposalMethod} onChange={e => setDisposalMethod(e.target.value)} rows={2} style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
          </div>
        </div>
      </Modal>

    </Layout>
  );
}